import type { ErrorBody } from "@repo/shared-types";

/** One part outside an event's new dates, as the error body lists it. */
export type PartConflict = NonNullable<ErrorBody["conflicts"]>[number];

/** An HTTP error from the api: any non-2xx response. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    message: string,
    /**
     * The request's field the message is about, when the api names one, so a
     * form can show it beside its box rather than above the form.
     */
    public readonly field?: string,
    /**
     * The parts in the way of an event's new dates, and what fitting them
     * does to each: the 409 of changing an event's start or end.
     */
    public readonly conflicts?: PartConflict[],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  timeoutMs?: number;
  /** Injection seam for tests, and for a native client with its own fetch. */
  fetchImpl?: typeof fetch;
  /** Merged ahead of a call's own headers, so a call site can still override. */
  getHeaders?: () => Record<string, string> | Promise<Record<string, string>>;
  /** Defaults applied to every request, e.g. `credentials` on a native client. */
  defaultInit?: RequestInit;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;
  private readonly fetchImpl: typeof fetch;
  private readonly getHeaders?: ApiClientOptions["getHeaders"];
  private readonly defaultInit: RequestInit;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    this.timeoutMs = options.timeoutMs ?? 10_000;
    // Bound, not stored bare: called later as `this.fetchImpl(...)` the
    // receiver would be this instance rather than the global, which browsers
    // reject with "Illegal invocation".
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
    this.getHeaders = options.getHeaders;
    this.defaultInit = options.defaultInit ?? {};
  }

  async request<T>(path: string, init?: RequestInit): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const injected = this.getHeaders ? await this.getHeaders() : {};
    const { signal, cancel } = this.timeoutSignal(init?.signal);

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        ...this.defaultInit,
        ...init,
        signal,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...this.defaultInit.headers,
          ...injected,
          ...init?.headers,
        },
      });
    } finally {
      cancel();
    }

    if (!response.ok) {
      const { message, field, conflicts } = await this.errorBody(response);
      throw new ApiError(response.status, response.statusText, message, field, conflicts);
    }

    // 204, and a 202 that only says "accepted" — asking for a reset link
    // answers nothing either way, so as not to say whether the address has
    // an account. An empty body is no value rather than a JSON error.
    const body = await response.text();
    if (body === "") {
      return undefined as T;
    }
    return JSON.parse(body) as T;
  }

  /**
   * The api answers every error with `{ statusCode, error, message }`, and
   * `field` when the message is about one, and `conflicts` when an event's
   * new dates leave parts outside. Reading `message` out of it is
   * what makes a sign-in failure render as "invalid email or password"
   * rather than as a line of JSON.
   */
  private async errorBody(
    response: Response,
  ): Promise<{ message: string; field?: string; conflicts?: PartConflict[] }> {
    const fallback = `${response.status} ${response.statusText}`;
    const body = await response.text().catch(() => "");
    if (!body) return { message: fallback };

    try {
      const parsed: unknown = JSON.parse(body);
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "message" in parsed &&
        typeof (parsed as { message: unknown }).message === "string"
      ) {
        const { message, field, conflicts } = parsed as {
          message: string;
          field?: unknown;
          conflicts?: unknown;
        };
        return {
          message,
          ...(typeof field === "string" && field !== "" ? { field } : {}),
          ...(Array.isArray(conflicts) ? { conflicts: conflicts.filter(isPartConflict) } : {}),
        };
      }
    } catch {
      // Not JSON — a proxy error page, say. Fall through to the raw body.
    }
    return { message: body };
  }

  /**
   * A caller's own signal wins. Otherwise a timeout, built from
   * AbortController rather than AbortSignal.timeout: the latter is missing on
   * some React Native runtimes, and this package is meant to run there too.
   */
  private timeoutSignal(provided: AbortSignal | null | undefined): {
    signal: AbortSignal;
    cancel: () => void;
  } {
    if (provided) return { signal: provided, cancel: () => {} };

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    return { signal: controller.signal, cancel: () => clearTimeout(timer) };
  }

  get<T>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>(path, { ...init, method: "GET" });
  }

  post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>(path, {
      ...init,
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>(path, {
      ...init,
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  patch<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>(path, {
      ...init,
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }

  delete<T>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>(path, { ...init, method: "DELETE" });
  }
}

/** A conflict as the api sends it: a part, which date, and from what to what. */
function isPartConflict(value: unknown): value is PartConflict {
  if (typeof value !== "object" || value === null) return false;
  const { partId, field, from, to } = value as Record<string, unknown>;
  return (
    typeof partId === "string" &&
    typeof field === "string" &&
    typeof from === "string" &&
    (to === null || typeof to === "string")
  );
}
